import { useNavigate } from "react-router-dom";
import { useResolvedImageUrl } from "../../hooks/useResolvedImageUrl";
import { useClientTranslations } from "../../lib/i18n/client";
import type { EducationContent } from "../../services/educationService";
import styles from "./LibraryCard.module.css";

// Figma play glyph 3830-20822 — purple outline play (the Figma "Play" node is
// 20px inside the 48px white circle). viewBox is cropped to the triangle so it
// fills the 20px box instead of rendering tiny.
const IconPlay = () => (
	<svg
		width="20"
		height="20"
		viewBox="30 16 18 20"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M32.4863 18.4966L44.1523 25.9961L32.4863 33.4957V18.4966Z"
			stroke="#5e60bf"
			strokeWidth="1.66657"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
const IconVideo = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 12 12"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M1.5 3.5C1.5 2.94772 1.94772 2.5 2.5 2.5H6.5C7.05228 2.5 7.5 2.94772 7.5 3.5V8.5C7.5 9.05228 7.05228 9.5 6.5 9.5H2.5C1.94772 9.5 1.5 9.05228 1.5 8.5V3.5Z"
			stroke="#5e60bf"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M7.5 5L10.5 3.5V8.5L7.5 7"
			stroke="#5e60bf"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
const IconHeadphones = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M3 14v-2a9 9 0 0 1 18 0v2"
			stroke="#5e60bf"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<rect
			x="3"
			y="14"
			width="4"
			height="6"
			rx="1"
			stroke="#5e60bf"
			strokeWidth="2"
		/>
		<rect
			x="17"
			y="14"
			width="4"
			height="6"
			rx="1"
			stroke="#5e60bf"
			strokeWidth="2"
		/>
	</svg>
);
// Figma node 3830-22347 — grey outline heart when not favorited; purple
// (filled) when favorited.
const IconHeart = ({ filled }: { filled: boolean }) => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M14.2461 10.4967C15.3632 9.40205 16.4954 8.08996 16.4954 6.37299C16.4954 5.27931 16.0609 4.23043 15.2875 3.45708C14.5142 2.68373 13.4653 2.24927 12.3716 2.24927C11.052 2.24927 10.1223 2.62415 8.99768 3.7488C7.87303 2.62415 6.94331 2.24927 5.62372 2.24927C4.53004 2.24927 3.48116 2.68373 2.70781 3.45708C1.93446 4.23043 1.5 5.27931 1.5 6.37299C1.5 8.09746 2.62465 9.40955 3.7493 10.4967L8.99768 15.7451L14.2461 10.4967Z"
			fill={filled ? "#5e60bf" : "none"}
			stroke={filled ? "#5e60bf" : "#c1c1c1"}
			strokeWidth="1.49954"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface LibraryCardProps {
	item: EducationContent;
	favorited: boolean;
	onToggleFavorite: (id: string) => void;
	/** Fired when the card is opened (click or keyboard). */
	onOpen?: (item: EducationContent) => void;
}

export function LibraryCard({
	item,
	favorited,
	onToggleFavorite,
	onOpen,
}: LibraryCardProps) {
	const navigate = useNavigate();
	const { t } = useClientTranslations("education");
	// Resolve auth-gated proxy images to a bearer-fetched blob.
	// loading/error/idle → the gradient fallback shows; loaded → hero.
	const { url: heroUrl } = useResolvedImageUrl(item.imageUrl);
	const isVideo = item.contentType === "video";
	const target = isVideo
		? `/education/${item.id}/video`
		: `/education/${item.id}/podcast`;
	const hasDuration = !!item.duration && item.duration !== "—";

	const open = () => {
		onOpen?.(item);
		navigate(target);
	};

	return (
		// biome-ignore lint/a11y/useSemanticElements: outer card cannot be <button> because it contains a nested <button> (heart); role="button" + keyboard handler is the correct accessible pattern here
		<div
			className={styles.card}
			role="button"
			tabIndex={0}
			onClick={open}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					open();
				}
			}}
		>
			<div
				className={styles.imgArea}
				style={
					heroUrl
						? {
								// Hero image first, gradient placeholder second — a failed
								// hero load still shows the placeholder (matches the .fallback
								// gradient used when no imageUrl is present).
								backgroundImage: `url(${heroUrl}), linear-gradient(132deg, #dedfff 21.7%, #5e60bf 100%)`,
								backgroundSize: "cover",
								backgroundPosition: "center",
							}
						: undefined
				}
			>
				{!heroUrl && <div className={styles.fallback} aria-hidden="true" />}
				<div className={styles.center}>
					<span className={styles.play}>
						<IconPlay />
					</span>
					<span className={styles.title}>{item.title}</span>
				</div>
			</div>
			<div className={styles.controlRow}>
				<div className={styles.controlLeft}>
					{hasDuration && (
						<span className={styles.durationPill}>{item.duration}</span>
					)}
					<span className={styles.typeIcon}>
						{isVideo ? <IconVideo /> : <IconHeadphones />}
					</span>
				</div>
				<button
					type="button"
					className={styles.heart}
					aria-pressed={favorited}
					aria-label={
						favorited
							? t("library.removeFavorite", "Remove from favorites")
							: t("library.addFavorite", "Add to favorites")
					}
					onClick={(e) => {
						e.stopPropagation();
						onToggleFavorite(item.id);
					}}
				>
					<IconHeart filled={favorited} />
				</button>
			</div>
		</div>
	);
}
